import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import AuditTrail from "./AuditTrail";
import PlaceholderComponent from "./PlaceholderComponent";

import _ from "underscore";
import * as loaderActions from "../../../store/actions/loaderActions";
import * as templateActions from "../../../store/actions/templateActions";
import APP_CONST from "../../../core/shared/constants";

const ArchiveDashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const jwtToken = sessionStorage.getItem("jwtToken");

  const {
    generateAuditTrailInfo,
    isCancelBeforeSuccess,
    showLoaderComponent,
    latestArchiveInfo,
    isCancelLoading,
    isInitialAuditCancel
  } = useSelector((store) => store.template);

  const {apiInitConfigObj} = useSelector((store) => store.zone);

  const [showPlaceholderComponent, setShowPlaceholderComponent] =
    useState(showLoaderComponent);

  // eslint-disable-next-line no-unused-vars
  const { percentage, status, latest, statusmessage } = generateAuditTrailInfo;

  useEffect(()=>{
      let relVer = (apiInitConfigObj.releaseversion !== undefined) ? `?i=`+apiInitConfigObj.releaseversion : "";
      navigate("/archive" + relVer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[apiInitConfigObj.releaseversion])

  /* Call the Audit trail detail API
   * @dependency jwtToken
   */
  useEffect(() => {
    if (jwtToken && _.isEmpty(generateAuditTrailInfo)) {
      dispatch(templateActions.getCompleteAuditTrails(APP_CONST.TEXT_AUDIT));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken]);

  useEffect(() => {
    if (generateAuditTrailInfo?.percentage === 0) {
      setShowPlaceholderComponent(true);
    }

    return () => {
      dispatch(loaderActions.hideError());

      dispatch(templateActions.generateCompleteAuditTrailFailed());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === "Cancelled" || status === "Completed") {
      setShowPlaceholderComponent(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  /* To show and hide the placeholder component
   * Redux variable are used
   */
  useEffect(() => {
    if (
      !_.isEmpty(generateAuditTrailInfo) &&
      !isCancelBeforeSuccess &&
      status
    ) {
      if (
        percentage >= 0 &&
        percentage < 100 &&
        !["Cancelled", "Failed", "Completed"].includes(status)
      ) {
        if (
          percentage > 0 &&
          percentage < 100 &&
          ["Started", "In-Progress"].includes(status) &&
          !isCancelLoading
        ) {
          setShowPlaceholderComponent(true);
        }
        setTimeout(() => {
          dispatch(
            templateActions.statusCompleteAuditTrail(generateAuditTrailInfo)
          );
        }, 3000);
      } else if (
        percentage === 100 &&
        status === "Completed" &&
        !_.isEmpty(latest)
      ) {
        setShowPlaceholderComponent(false);

        dispatch(
          loaderActions.showError(
            t("ARCHIVE.TOAST_GENERATESUCCESS"),
            "info",
            "information"
          )
        );
      } else if (status === "Failed") {
        let findHashValue = generateAuditTrailInfo.statusmessage.includes("#");
        if (findHashValue && findHashValue === true) {
          let isUniqueIdError = generateAuditTrailInfo.statusmessage.split("#");
          if (
            isUniqueIdError &&
            isUniqueIdError[0] === "Invalid Unique Id for artifact "
          ) {
            let errormsg =
              t("ARCHIVE.TOAST_UNIQUEIDERROR") +
              isUniqueIdError
                .slice(1)
                .map((ids) => ids)
                .join("#");
            dispatch(loaderActions.showError(errormsg));
          } else {
            dispatch(
              loaderActions.showError(
                t("ARCHIVE.TOAST_GENERATESOMETHINGWENTWRONG"),
                "error",
                "warning"
              )
            );
          }
        } else {
          dispatch(
            loaderActions.showError(
              t("ARCHIVE.TOAST_GENERATESOMETHINGWENTWRONG", "error", "warning")
            )
          );
        }
      }
    } else if (
      !_.isEmpty(generateAuditTrailInfo) &&
      !isInitialAuditCancel &&
      isCancelBeforeSuccess
    ) {
      dispatch(
        templateActions.cancelCompleteAuditTrail(
          {
            id: generateAuditTrailInfo.archiveId,
          },
          true
        )
      );
      dispatch(templateActions.setInitialAuditCancel(true))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateAuditTrailInfo, latestArchiveInfo]);

  // To cancel the already ongoing File Generation API call
  const cancelGenerateAuditTrail = () => {
    if (!_.isEmpty(generateAuditTrailInfo.archiveId)) {
      dispatch(
        templateActions.cancelCompleteAuditTrail({
          id: generateAuditTrailInfo.archiveId,
        })
      );
    } else {
      dispatch(templateActions.cancelCompleteAuditTrailBeforeSuccess());
    }
  };

  // Render the Audit trail component in return method
  const renderAuditTrail = () => {
    return (
      <section className="archive-dashboard">
        <div className="pb-30 bb-1 pl-30 pr-30">
          <h3>{t("ARCHIVE.TEXT_AUDITTRAILREPORT")}</h3>
          <p className="light-grey lh1_5">
            {t("ARCHIVE.TEXT_ARCHIVEDOWNLOADFILEGENERATEMESSAGE")}
          </p>
        </div>

        <AuditTrail setShowPlaceholderComponent={setShowPlaceholderComponent} />
      </section>
    );
  };

  //Render the Placeholder Component in return method
  const renderPlaceholder = () => {
    return (
      <PlaceholderComponent
        title={t("ARCHIVE.TEXT_GENERATEFILESEVERALMIN")}
        subTitle={t("ARCHIVE.TEXT_GENERATEDOWNLOADMSG")}
        processValue={percentage}
        setShowPlaceholderComponent={setShowPlaceholderComponent}
        cancelHandler={cancelGenerateAuditTrail}
        bottomSpinner
        loader
      />
    );
  };

  return (
    <>
      {!showPlaceholderComponent && renderAuditTrail()}
      {status !== ("Completed" || "Cancelled") &&
        showPlaceholderComponent &&
        renderPlaceholder()}
    </>
  );
};

export default ArchiveDashboard;
